/**
 * Composable for handling browser initialization with proper timing
 *
 * This composable solves the race condition issues with browser initialization
 * by providing a clean, predictable initialization pattern that:
 * 1. Ensures single initialization per modal session
 * 2. Handles timing issues between modal animation and component mounting
 * 3. Provides proper error handling and retry logic
 * 4. Eliminates the need for setTimeout workarounds
 */

import { ref, Ref, nextTick, onUnmounted } from 'vue';

export interface BrowserInstance {
  initialize?: (params?: unknown) => Promise<void>;
  [key: string]: unknown;
}

export interface UseBrowserInitializationOptions<T extends BrowserInstance> {
  /**
   * Reference to the browser component
   */
  browserRef: Ref<T | undefined>;

  /**
   * Function to get initialization parameters
   * Called when initialization is needed
   */
  getInitParams?: () => unknown;

  /**
   * Optional callback after successful initialization
   */
  onInitialized?: () => void | Promise<void>;

  /**
   * Optional callback on initialization error
   */
  onError?: (error: Error) => void;

  /**
   * Maximum number of retry attempts
   * @default 3
   */
  maxRetries?: number;

  /**
   * Delay between retry attempts in ms
   * @default 50
   */
  retryDelay?: number;
}

export interface UseBrowserInitializationReturn {
  /**
   * Whether the browser is currently initialized
   */
  isInitialized: Ref<boolean>;

  /**
   * Whether initialization is in progress
   */
  isInitializing: Ref<boolean>;

  /**
   * Initialize the browser with proper timing and error handling
   */
  initializeBrowser: () => Promise<void>;

  /**
   * Reset initialization state (called on modal hide)
   */
  resetInitialization: () => void;

  /**
   * Last initialization error if any
   */
  initError: Ref<Error | null>;
}

/**
 * Composable for browser initialization with race condition handling
 */
export function useBrowserInitialization<T extends BrowserInstance>(
  options: UseBrowserInitializationOptions<T>
): UseBrowserInitializationReturn {
  const {
    browserRef,
    getInitParams,
    onInitialized,
    onError,
    maxRetries = 3,
    retryDelay = 50,
  } = options;

  // State
  const isInitialized = ref(false);
  const isInitializing = ref(false);
  const initError = ref<Error | null>(null);
  const initializationPromise = ref<Promise<void> | null>(null);

  // Cleanup flag to prevent operations after component unmount
  let isUnmounted = false;

  /**
   * Wait for browser ref to be available with retry logic
   */
  async function waitForBrowserRef(attempts = 0): Promise<T> {
    if (isUnmounted) {
      throw new Error('Component unmounted during initialization');
    }

    // Check if browser ref is available
    if (browserRef.value) {
      return browserRef.value;
    }

    // Max attempts reached
    if (attempts >= maxRetries) {
      throw new Error('Browser component not available after maximum retries');
    }

    // Wait and retry using requestAnimationFrame for predictable timing
    await new Promise((resolve) => {
      // Convert delay to frame count (~16ms per frame)
      let frames = Math.ceil(retryDelay / 16);
      const nextFrame = () => {
        if (--frames <= 0) resolve(void 0);
        else requestAnimationFrame(nextFrame);
      };
      requestAnimationFrame(nextFrame);
    });
    await nextTick();

    return waitForBrowserRef(attempts + 1);
  }

  /**
   * Initialize the browser with proper error handling and retry logic
   */
  async function initializeBrowser(): Promise<void> {
    // If already initialized or initializing, return existing promise
    if (isInitialized.value) {
      return Promise.resolve();
    }

    if (isInitializing.value && initializationPromise.value) {
      return initializationPromise.value;
    }

    // Create new initialization promise
    initializationPromise.value = performInitialization();
    return initializationPromise.value;
  }

  /**
   * Perform the actual initialization
   */
  async function performInitialization(): Promise<void> {
    isInitializing.value = true;
    initError.value = null;

    try {
      // Wait for browser to be available
      const browser = await waitForBrowserRef();

      if (isUnmounted) {
        return;
      }

      // Get initialization parameters
      const params = getInitParams?.();

      // Initialize the browser
      if (browser.initialize) {
        await browser.initialize(params);
      }

      if (isUnmounted) {
        return;
      }

      // Mark as initialized
      isInitialized.value = true;

      // Call success callback
      await onInitialized?.();
    } catch (error) {
      const err = error as Error;
      initError.value = err;

      // Call error callback
      onError?.(err);

      // Log error for debugging

      throw err;
    } finally {
      isInitializing.value = false;
      initializationPromise.value = null;
    }
  }

  /**
   * Reset initialization state (typically called on modal hide)
   */
  function resetInitialization() {
    isInitialized.value = false;
    isInitializing.value = false;
    initError.value = null;
    initializationPromise.value = null;
  }

  // Cleanup on unmount
  onUnmounted(() => {
    isUnmounted = true;
    resetInitialization();
  });

  return {
    isInitialized,
    isInitializing,
    initError,
    initializeBrowser,
    resetInitialization,
  };
}

/**
 * Enhanced modal lifecycle hooks with browser initialization
 *
 * This combines modal lifecycle management with browser initialization
 * to provide a clean, race-condition-free initialization pattern.
 */
export interface EnhancedModalLifecycleOptions<T extends BrowserInstance> {
  /**
   * Browser initialization options
   */
  browserOptions: UseBrowserInitializationOptions<T>;

  /**
   * Function to load data before initialization
   */
  loadData?: () => Promise<void>;

  /**
   * Additional onShow callback
   */
  onShow?: () => void | Promise<void>;

  /**
   * Additional onHide callback
   */
  onHide?: () => void | Promise<void>;

  /**
   * Additional onShown callback
   */
  onShown?: () => void | Promise<void>;

  /**
   * Event emitter
   */
  emit?: (event: string, ...args: unknown[]) => void;
}

/**
 * Enhanced modal lifecycle with integrated browser initialization
 */
export function useEnhancedModalLifecycle<T extends BrowserInstance>(
  options: EnhancedModalLifecycleOptions<T>
) {
  const {
    browserOptions,
    loadData,
    onShow: customOnShow,
    onHide: customOnHide,
    onShown: customOnShown,
    emit,
  } = options;

  // Initialize browser management
  const browserInit = useBrowserInitialization(browserOptions);

  /**
   * Handle modal show event
   */
  async function onShow() {
    // Reset initialization state
    browserInit.resetInitialization();

    // Load data if needed
    if (loadData) {
      await loadData();
    }

    // Custom onShow handler
    await customOnShow?.();

    // Emit show event
    emit?.('show');

    // Start initialization process (non-blocking)
    // This will continue in the background and complete in onShown
    browserInit.initializeBrowser().catch(() => {
      // Background initialization failed - error handled silently
    });
  }

  /**
   * Handle modal shown event (after animation completes)
   */
  async function onShown() {
    // Ensure initialization completes
    try {
      await browserInit.initializeBrowser();
    } catch (error) {
      // Initialization failed in onShown - error handled silently
    }

    // Custom onShown handler
    await customOnShown?.();
  }

  /**
   * Handle modal hide event
   */
  async function onHide() {
    // Custom onHide handler
    await customOnHide?.();

    // Reset initialization state
    browserInit.resetInitialization();

    // Emit hide event
    emit?.('hide');
  }

  return {
    onShow,
    onShown,
    onHide,
    ...browserInit,
  };
}
